import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getApiUrl } from '../config/api';
import './ParrotAutoRank.css';

const ParrotAutoRank = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('keywords');
  const [apiKeys, setApiKeys] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingKeywords, setLoadingKeywords] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsModal, setDetailsModal] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('scanmaster_token');

  const fetchApiKeys = useCallback(async () => {
    setLoadingKeys(true);
    setError('');
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Please sign in to view API keys.');
        setLoadingKeys(false);
        return;
      }
      const res = await fetch(getApiUrl('user/api-keys'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch API keys');
      }
      const data = await res.json();
      setApiKeys(data.api_keys || []);
    } catch (err) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  const fetchKeywords = useCallback(async () => {
    setLoadingKeywords(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setLoadingKeywords(false);
        return;
      }
      const res = await fetch(getApiUrl('user/integrations/parrot-seo-ai-engine/keywords'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch keywords');
      }
      const data = await res.json();
      setKeywords(data.keywords || []);
    } catch (err) {
      setKeywords([]);
    } finally {
      setLoadingKeywords(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    setNewlyCreatedKey(null);
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Please sign in to create an API key.');
        setCreating(false);
        return;
      }
      const res = await fetch(getApiUrl('user/api-keys'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newKeyName.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create API key');
      }
      setNewlyCreatedKey(data);
      setNewKeyName('');
      setSuccess('API key created. Copy it now — it will not be shown again.');
      fetchApiKeys();
    } catch (err) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this API key? Any plugin using it will stop working.')) return;
    setError('');
    setSuccess('');
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Please sign in.');
        return;
      }
      const res = await fetch(getApiUrl(`user/api-keys/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to revoke API key');
      }
      setSuccess('API key revoked.');
      fetchApiKeys();
    } catch (err) {
      setError(err.message || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard.');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleShowDetails = async (keywordId) => {
    setDetailsLoading(true);
    setDetailsModal(null);
    try {
      const token = getAuthToken();
      if (!token) return;
      const res = await fetch(getApiUrl(`user/integrations/parrot-seo-ai-engine/keywords/${keywordId}/details`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load details');
      setDetailsModal(data);
    } catch (err) {
      setError(err.message || 'Failed to load keyword details');
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div className="parrot-auto-rank-page">
      <button type="button" className="back-btn" onClick={() => navigate('/dashboard/integrations')}>
        ← Back to Integrations
      </button>

      <div className="parrot-auto-rank-header">
        <h1>Parrot SEO AI Engine</h1>
        <p className="parrot-auto-rank-subtitle">
          Connect your WordPress site via API key. Keywords sent from the plugin appear below.
        </p>
      </div>

      {error && <div className="parrot-auto-rank-error">{error}</div>}
      {success && <div className="parrot-auto-rank-success">{success}</div>}

      {newlyCreatedKey && (
        <div className="new-key-alert">
          <strong>New API Key (copy now — it will not be shown again):</strong>
          <div className="new-key-value">
            <code>{newlyCreatedKey.api_key}</code>
            <button type="button" onClick={() => copyToClipboard(newlyCreatedKey.api_key)}>
              Copy
            </button>
          </div>
          <button type="button" className="dismiss-new-key" onClick={() => setNewlyCreatedKey(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="parrot-tabs">
        <button
          type="button"
          className={`parrot-tab ${activeTab === 'keywords' ? 'active' : ''}`}
          onClick={() => setActiveTab('keywords')}
        >
          Keywords
        </button>
        <button
          type="button"
          className={`parrot-tab ${activeTab === 'api-keys' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-keys')}
        >
          API Keys
        </button>
      </div>

      {activeTab === 'keywords' && (
        <section className="parrot-section keywords-section">
          <h2>Keywords from Auto-Rank</h2>
          <p className="section-desc">
            Keywords sent from the Parrot SEO AI Engine plugin when analyzing pages.
          </p>
          {loadingKeywords ? (
            <p className="loading-text">Loading…</p>
          ) : keywords.length === 0 ? (
            <p className="empty-text">No keywords yet. Connect the plugin and run content analysis.</p>
          ) : (
            <table className="keywords-table">
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th>URL</th>
                  <th>Rank</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((k) => (
                  <tr key={k.id}>
                    <td className="keyword-cell">{k.keyword}</td>
                    <td>
                      <a href={k.page_url} target="_blank" rel="noopener noreferrer" className="keyword-url">
                        {k.page_url}
                      </a>
                    </td>
                    <td>#{k.current_rank ?? '—'}</td>
                    <td>{k.last_updated ? new Date(k.last_updated).toLocaleString() : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="details-btn"
                        onClick={() => handleShowDetails(k.id)}
                        disabled={detailsLoading}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {detailsModal && (
        <div className="keyword-details-overlay" onClick={() => setDetailsModal(null)}>
          <div className="keyword-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="keyword-details-header">
              <h3>Keyword Details</h3>
              <button type="button" className="modal-close" onClick={() => setDetailsModal(null)}>×</button>
            </div>
            <div className="keyword-details-body">
              <div className="details-summary">
                <p><strong>Keyword:</strong> {detailsModal.keyword}</p>
                <p><strong>URL:</strong> <a href={detailsModal.page_url} target="_blank" rel="noopener noreferrer">{detailsModal.page_url}</a></p>
                <div className="rank-badges">
                  <span className="rank-badge"><strong>Initial Rank:</strong> #{detailsModal.initial_rank}</span>
                  <span className="rank-badge current"><strong>Current Rank:</strong> #{detailsModal.current_rank}</span>
                </div>
              </div>
              <div className="details-chart">
                <h4>Rank Growth</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={detailsModal.rank_history} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis reversed domain={[1, 50]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`#${v}`, 'Rank']} />
                    <Line type="monotone" dataKey="rank" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="details-competitors">
                <h4>Top 10 Competitors</h4>
                <table className="competitors-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Domain</th>
                      <th>Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailsModal.competitors?.map((c, i) => (
                      <tr key={c.domain}>
                        <td>{i + 1}</td>
                        <td>{c.domain}</td>
                        <td>#{c.rank}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'api-keys' && (
        <>
          <section className="parrot-section api-keys-section">
            <h2>API Keys</h2>
            <p className="section-desc">
              Create API keys to authenticate the Parrot SEO AI Engine WordPress plugin.
            </p>

            <form onSubmit={handleCreateKey} className="create-key-form">
              <input
                type="text"
                placeholder="Key name (optional, e.g. WordPress - mysite.com)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                disabled={creating}
              />
              <button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create API Key'}
              </button>
            </form>

            <div className="api-keys-list">
              {loadingKeys ? (
                <p className="loading-text">Loading…</p>
              ) : apiKeys.length === 0 ? (
                <p className="empty-text">No API keys yet. Create one above.</p>
              ) : (
                <table className="api-keys-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Status</th>
                      <th>Last Used</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((k) => (
                      <tr key={k.id}>
                        <td>{k.name || '—'}</td>
                        <td><code>{k.key_preview}</code></td>
                        <td>
                          <span className={`status-badge ${k.status}`}>{k.status}</span>
                        </td>
                        <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}</td>
                        <td>{k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}</td>
                        <td>
                          {k.status === 'active' && (
                            <button
                              type="button"
                              className="revoke-btn"
                              onClick={() => handleRevoke(k.id)}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="parrot-section api-endpoints-section">
            <h2>API Endpoints</h2>
            <p className="section-desc">
              Base URL: <code>{getApiUrl('v1')}</code>
            </p>
            <ul>
              <li><strong>POST /api/v1/test-connection</strong> — Test API connectivity</li>
              <li><strong>POST /api/v1/content/analyze</strong> — Send page content for analysis</li>
            </ul>
            <p>
              Include the API key in the header: <code>Authorization: Bearer {'{api_key}'}</code>
            </p>
          </section>
        </>
      )}
    </div>
  );
};

export default ParrotAutoRank;
